import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { FaTimes, FaShoppingCart } from "react-icons/fa";

export default function MainNav() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      const count = cart.reduce((sum, item) => sum + item.quantity, 0);
      setCartCount(count);
    };
    
    updateCartCount();
    window.addEventListener("cartUpdated", updateCartCount);
    return () => window.removeEventListener("cartUpdated", updateCartCount);
  }, []);

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/management", label: "Management" },
    { path: "/promotion", label: "Promotion" },
    { path: "/recordings", label: "Recordings" },
    { path: "/shop", label: "Shop" },
    { path: "/news", label: "News" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-zinc-800">
      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
        {/* Desktop: Logo links home | Mobile: Logo opens menu */}
        <Link 
          to="/" 
          className="hidden md:flex items-center gap-3" 
          data-testid="nav-logo"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_music-connect-23/artifacts/mbsj96z9_UNDERPLAY%20MUSIC%20LOGO%20NEW%202025%20no%20border%20no%20TEXT.png" 
            alt="Underplay" 
            className="h-6 w-auto"
          />
        </Link>
        
        {/* Mobile: Logo toggles menu */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden flex items-center gap-3"
          data-testid="nav-logo-mobile"
        >
          {mobileMenuOpen ? (
            <FaTimes size={20} className="text-zinc-400" />
          ) : (
            <img 
              src="https://customer-assets.emergentagent.com/job_music-connect-23/artifacts/mbsj96z9_UNDERPLAY%20MUSIC%20LOGO%20NEW%202025%20no%20border%20no%20TEXT.png" 
              alt="Underplay" 
              className="h-6 w-auto"
            />
          )}
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8" data-testid="main-nav-desktop">
          {navItems.filter(item => item.path !== "/").map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={`text-sm transition-colors ${
                isActive(item.path)
                  ? "text-white"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <Link 
            to="/shop/cart" 
            data-testid="nav-cart" 
            className="relative text-zinc-400 hover:text-white transition-colors"
          >
            <FaShoppingCart size={18} />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-white text-black text-xs rounded-full flex items-center justify-center font-bold">
                {cartCount}
              </span>
            )}
          </Link>
          <Link to="/admin" data-testid="nav-link-admin" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">
            Admin
          </Link>
        </div>

        {/* Mobile: Cart only */}
        <div className="md:hidden flex items-center gap-4">
          <Link 
            to="/shop/cart" 
            className="relative text-zinc-400 hover:text-white transition-colors"
          >
            <FaShoppingCart size={18} />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-white text-black text-xs rounded-full flex items-center justify-center font-bold">
                {cartCount}
              </span>
            )}
          </Link>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-zinc-950 border-b border-zinc-800 relative" style={{ zIndex: 10001 }}>
          <div className="px-6 py-6 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block text-xl py-4 ${
                  isActive(item.path)
                    ? "text-white"
                    : "text-zinc-400 active:text-white"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/admin"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm text-zinc-600 py-4 mt-4 border-t border-zinc-800 pt-6"
            >
              Admin
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
